import { useMemo } from 'react'
import type { CustomerAccount, CustomerProfile } from '../types'
import { formatDateTime, getInitials } from '../helpers/formatters'
import type { ShopLocale, ShopMessageKey } from '../../i18n'

type TranslateFn = (key: ShopMessageKey, params?: Record<string, string | number>) => string

export type CustomerDashboardSummary = {
  displayName: string
  initials: string
  email: string
  username: string
  lastLoginLabel: string
  deviceLabel: string
}

export const useCustomerDashboardSummary = ({
  account,
  profile,
  locale,
  t,
}: {
  account: CustomerAccount
  profile: CustomerProfile
  locale: ShopLocale
  t: TranslateFn
}) =>
  useMemo<CustomerDashboardSummary>(
    () => {
      const fullName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim()
      const displayName = fullName || profile.displayName || account.username

      return {
        displayName,
        initials: getInitials(displayName),
        email: profile.email,
        username: account.username,
        lastLoginLabel: formatDateTime(account.lastLoginAt, locale, t('common.never')),
        deviceLabel: account.lastLoginDeviceName || t('customer.dashboard.deviceUnavailable'),
      }
    },
    [
      account.lastLoginAt,
      account.lastLoginDeviceName,
      account.username,
      locale,
      profile.displayName,
      profile.email,
      profile.firstName,
      profile.lastName,
      t,
    ],
  )
